import type { Component } from 'vue';
import { RouteRecordRaw } from 'vue-router';

// 動的インポート関数 - Vue Component型を使用
const About = (): Promise<Component> => import('@/pages/candidate/AboutPage.vue');
const Login = (): Promise<Component> => import('@/pages/candidate/LoginPage.vue');
const ForgotPassword = (): Promise<Component> => import('@/pages/candidate/ForgotPassword.vue');
const ResetPassword = (): Promise<Component> => import('@/pages/candidate/ResetPassword.vue');
const CompletedResetPassword = (): Promise<Component> =>
  import('@/pages/candidate/CompletedResetPassword.vue');
const CompletedBasicInfo = (): Promise<Component> =>
  import('@/pages/candidate/CompletedBasicInfo.vue');
const ProfilesEditPage = (): Promise<Component> =>
  import('@/pages/profiles-edit/ProfilesEditPage.vue');

const RegisterComplete = (): Promise<Component> =>
  import('@/pages/candidate/CompleteRegisterEmail.vue');
const ForgotPasswordComplete = (): Promise<Component> =>
  import('@/pages/candidate/CompleteForgotPassword.vue');
const VerifyPage = (): Promise<Component> => import('@/pages/candidate/VerifyPage.vue');
const VerifyResetPasswordPage = (): Promise<Component> =>
  import('@/pages/candidate/VerifyResetPasswordPage.vue');
const OfferReport = (): Promise<Component> =>
  import('@/pages/candidate/offer_report/OfferReport.vue');
const CompletedOfferReport = (): Promise<Component> =>
  import('@/pages/candidate/offer_report/Completed.vue');
const FAQ = (): Promise<Component> => import('@/pages/candidate/FAQPage.vue');
const TermAndCondition = (): Promise<Component> =>
  import('@/pages/candidate/TermAndConditionPage.vue');
const PrivacyPolicy = (): Promise<Component> => import('@/pages/candidate/PrivacyPolicyPage.vue');
const Corporate = (): Promise<Component> => import('@/pages/corporate/CorporatePage.vue');
const NotFoundPage = (): Promise<Component> => import('@/pages/candidate/NotFoundPage.vue');
const AnnouncementDetail = (): Promise<Component> => import('@/pages/announcements/[id]/Index.vue');
const BasicInformationUpdatePassword = (): Promise<Component> =>
  import('@/pages/candidate/register/basic_information/UpdatePassword.vue');
const JobsPage = (): Promise<Component> => import('@/pages/jobs/JobsPage.vue');
const SignUpDirection = (): Promise<Component> =>
  import('@/pages/candidate/register/basic_information/SignUpDirection.vue');
const CpContact = (): Promise<Component> => import('@/pages/candidate/CpContact.vue');
const CpContactDone = (): Promise<Component> => import('@/pages/candidate/CpContactDone.vue');
const Contact = (): Promise<Component> => import('@/pages/candidate/Contact.vue');
const ContactDone = (): Promise<Component> => import('@/pages/candidate/ContactDone.vue');
const TeaserThanks = (): Promise<Component> => import('@/pages/teaser/thanks.vue');
const Partners = (): Promise<Component> => import('@/pages/partners.vue');
const Business = (): Promise<Component> => import('@/pages/business.vue');
const TopEntry = (): Promise<Component> => import('@/pages/top/TopEntryPage.vue');
const ConsiderationPage = (): Promise<Component> =>
  import('@/pages/consideration/ConsiderationPage.vue');
const FavoritePage = (): Promise<Component> => import('@/pages/favorite/FavoritePage.vue');
const MessagesPage = (): Promise<Component> => import('@/pages/messages/MessagesPage.vue');
const ScoutPage = (): Promise<Component> => import('@/pages/scouts/ScoutPage.vue');
const RecommendedJobsPage = (): Promise<Component> =>
  import('@/pages/recommended-jobs/RecommendedJobsPage.vue');
const CompanyPage = (): Promise<Component> => import('@/pages/company/CompanyPage.vue');
const JobDetailPage = (): Promise<Component> => import('@/pages/jobs/detail/JobDetailPage.vue');
const StoryDetailPage = (): Promise<Component> =>
  import('@/pages/company/stories/detail/StoryDetailPage.vue');
const CompanyStoriesPage = (): Promise<Component> =>
  import('@/pages/company/stories/CompanyStoriesPage.vue');

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'top',
    components: {
      default: TopEntry,
    },
  },
  {
    path: '/pages/top/auth',
    redirect: '/',
  },
  {
    path: '/pages/top/guest',
    redirect: '/',
  },
  {
    path: '/agent/auth/consideration_jobs',
    name: 'consideration_jobs',
    components: {
      default: ConsiderationPage,
    },
  },
  {
    path: '/favorites/received',
    name: 'favorites_received',
    components: {
      default: FavoritePage,
    },
  },
  {
    path: '/favorites/sent',
    name: 'favorites_sent',
    components: {
      default: FavoritePage,
    },
  },
  {
    path: '/favorites/matched',
    name: 'favorites_matched',
    components: {
      default: FavoritePage,
    },
  },
  {
    path: '/signup/new',
    meta: { requiresGuest: true },
    components: {
      default: SignUpDirection,
    },
  },
  {
    path: '/announcements/:id',
    name: 'announcement_detail',
    components: {
      default: AnnouncementDetail,
    },
    props: true,
  },
  {
    path: '/candidate/signup/update_password',
    components: {
      default: BasicInformationUpdatePassword,
    },
  },
  {
    path: '/candidate/about',
    name: 'about',
    components: {
      default: About,
    },
  },
  {
    path: '/candidate/login',
    name: 'login',
    meta: { requiresGuest: true },
    components: {
      default: Login,
    },
  },
  {
    path: '/candidate/forgot_password',
    name: 'forgot_password',
    meta: { requiresGuest: true },
    components: {
      default: ForgotPassword,
    },
  },
  {
    path: '/candidate/reset_password',
    name: 'reset_password',
    meta: { requiresGuest: true },
    components: {
      default: ResetPassword,
    },
  },
  {
    path: '/candidate/reset_password/completed',
    name: 'reset_password_completed',
    components: {
      default: CompletedResetPassword,
    },
  },
  {
    path: '/candidate/completed_information',
    name: 'completed_information',
    components: {
      default: CompletedBasicInfo,
    },
  },
  {
    path: '/teaser/thanks',
    name: 'teaser_thanks',
    components: {
      default: TeaserThanks,
    },
  },
  {
    path: '/candidate/register_complete',
    name: 'register_complete',
    components: {
      default: RegisterComplete,
    },
  },
  {
    path: '/candidate/forgot_password_complete/:email',
    name: 'forgot_password_complete',
    components: {
      default: ForgotPasswordComplete,
    },
    props: true,
  },
  {
    path: '/profiles/edit',
    name: 'edit_profile',
    components: {
      default: ProfilesEditPage,
    },
  },
  {
    path: '/candidate/verify',
    name: 'verify',
    components: {
      default: VerifyPage,
    },
  },
  {
    path: '/candidate/verify_reset_password',
    name: 'verify_reset_password',
    components: {
      default: VerifyResetPasswordPage,
    },
  },
  {
    path: '/offer_report',
    name: 'offer_report',
    components: {
      default: OfferReport,
    },
  },
  {
    path: '/offer_report/fin',
    name: 'completed_offer_report',
    components: {
      default: CompletedOfferReport,
    },
  },
  {
    path: '/faq',
    name: 'faq',
    components: {
      default: FAQ,
    },
  },
  {
    path: '/terms',
    name: 'terms',
    components: {
      default: TermAndCondition,
    },
  },
  {
    path: '/privacy',
    name: 'privacy',
    components: {
      default: PrivacyPolicy,
    },
  },
  {
    path: '/corporate',
    name: 'corporate',
    components: {
      default: Corporate,
    },
  },
  {
    path: '/partners',
    name: 'Partners',
    component: Partners,
  },
  {
    path: '/business',
    name: 'business',
    component: Business,
  },
  {
    path: '/company/:companyId/stories',
    name: 'company_stories',
    components: {
      default: CompanyStoriesPage,
    },
    props: true,
  },
  {
    path: '/jobs',
    name: 'jobs_direction',
    components: {
      default: JobsPage,
    },
  },
  {
    path: '/jobs/agent/:pathMatch(.*)*',
    name: 'jobs_direction_agent',
    components: {
      default: JobsPage,
    },
  },
  {
    path: '/jobs/direct/:pathMatch(.*)*',
    name: 'jobs_direction_direct',
    components: {
      default: JobsPage,
    },
  },
  {
    path: '/jobs/:id(\\d+)',
    name: 'job_detail',
    components: {
      default: JobDetailPage,
    },
    props: { default: true },
  },
  {
    path: '/company/:id/info',
    name: 'company_info',
    components: {
      default: CompanyPage,
    },
    props: { default: true },
  },
  {
    path: '/company/:companyId/stories/:storyId',
    name: 'company_story_detail',
    components: {
      default: StoryDetailPage,
    },
    props: { default: true },
  },
  {
    path: '/cp-contact',
    name: 'cp_contact',
    components: {
      default: CpContact,
    },
  },
  {
    path: '/cp-contact/done',
    name: 'cp_contact_done',
    components: {
      default: CpContactDone,
    },
  },
  {
    path: '/contact',
    name: 'contact',
    components: {
      default: Contact,
    },
  },
  {
    path: '/contact/done',
    name: 'contact_done',
    components: {
      default: ContactDone,
    },
  },
  {
    path: '/messages',
    name: 'messages',
    components: { default: MessagesPage },
  },
  {
    path: '/messages/:id',
    name: 'messages_show',
    components: { default: MessagesPage },
    props: true,
  },
  {
    path: '/scouts',
    name: 'scouts',
    components: { default: ScoutPage },
  },
  {
    path: '/scouts/:id',
    name: 'scouts_show',
    components: { default: ScoutPage },
    props: true,
  },
  {
    path: '/recommended-jobs',
    name: 'recommended_jobs',
    components: { default: RecommendedJobsPage },
  },
  {
    path: '/recommended-jobs/:id',
    name: 'recommended_jobs_show',
    components: { default: RecommendedJobsPage },
    props: true,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not_found',
    components: {
      default: NotFoundPage,
    },
  },
];

export default routes;
